import React from "react";

import type { SelectChangeEvent } from "@mui/material/Select";
import Typography from "@mui/material/Typography";

import useIntegrationSettings from "@app/analytics/hooks/integrations/useIntegrationSettings";
import LastPushState from "@app/analytics/Integrations/components/LastPushState";
import SettingSwitch, {
  SettingSwitchProps,
} from "@app/analytics/Integrations/components/SettingSwitch";
import { INTEGRATION_PROVIDERS_BY_ID } from "@app/common/constants/providers";
import { toast } from "@app/common/hooks/useToasts";
import { Integration } from "@app/common/types";
import type {
  SalesforcePushIntegrationSetting,
  SalesforceNewContactAction,
  SalesforcePushSettings,
  UpdateIntegrationSettingRequestBody,
} from "@app/common/types/integrationSettingsTypes";

import Dropdown, { DropdownMenuItem } from "@rui/Dropdown";
import { getSelectedFromOptions } from "@rui/Dropdown/utils";
import InfoPanel from "@rui/InfoPanel";
import { Column, Row } from "@rui/Stack";

const CONTACT_TYPE_OPTIONS: {
  label: string;
  value: SalesforceNewContactAction;
}[] = [
  {
    label: "Create new Lead",
    value: "create_lead",
  },
  {
    label: "Create new Contact",
    value: "create_contact",
  },
];

const ConnectedContent = ({
  integrationSettings,
}: {
  integrationSettings: Integration["integrationSettings"];
}) => {
  const { updateIntegrationSetting } = useIntegrationSettings();

  const pushSetting = integrationSettings.byId
    .salesforce_push as SalesforcePushIntegrationSetting;
  const pullSetting = integrationSettings.byId.salesforce_pull;

  const displayName = INTEGRATION_PROVIDERS_BY_ID.salesforce.displayName;

  const handleUpdateSetting = (body: UpdateIntegrationSettingRequestBody) => {
    updateIntegrationSetting.mutate(body, {
      onError: () => {
        toast.error(
          "Something went wrong and your changes could not be saved. Please try again later or contact support@read.ai if the problem persists."
        );
      },
    });
  };

  const handleTogglePushEnabled: SettingSwitchProps["onChange"] = (
    _e,
    checked
  ) => {
    handleUpdateSetting({
      integrationKind: "salesforce_push",
      enabled: checked,
      providerSpecificSettings: pushSetting?.providerSpecificSettings,
    });
  };

  const handleTogglePullEnabled: SettingSwitchProps["onChange"] = (
    _e,
    checked
  ) => {
    handleUpdateSetting({
      integrationKind: "salesforce_pull",
      enabled: checked,
      providerSpecificSettings: pullSetting?.providerSpecificSettings,
    });
  };

  const handleUpdatePushSettings = (settings: SalesforcePushSettings) => {
    handleUpdateSetting({
      integrationKind: "salesforce_push",
      enabled: Boolean(pushSetting?.enabled),
      providerSpecificSettings: {
        ...pushSetting?.providerSpecificSettings,
        ...settings,
      },
    });
  };

  const handleToggleCreateNewEntries: SettingSwitchProps["onChange"] = (
    _e,
    checked
  ) => {
    handleUpdatePushSettings({
      newContactAction: checked ? "create_contact" : "do_nothing",
    });
  };

  const handleChangeContactType = (e: SelectChangeEvent) => {
    handleUpdatePushSettings({
      newContactAction: e.target
        .value as SalesforcePushSettings["newContactAction"],
    });
  };

  const handleToggleIncludeTranscript: SettingSwitchProps["onChange"] = (
    _e,
    checked
  ) => {
    handleUpdatePushSettings({ includeTranscript: checked });
  };

  const newContactAction =
    pushSetting?.providerSpecificSettings.newContactAction ?? "create_lead";
  const createNewEntriesEnabled = newContactAction !== "do_nothing";

  return (
    <>
      <Typography variant="bodyPrimaryMed" color="shades.neutral.700">
        Settings
      </Typography>
      <Column spacing={2}>
        <SettingSwitch
          title={`Auto-Push Meeting Notes to ${displayName}`}
          description={`Automatically update ${displayName} with meeting notes and participant information`}
          checked={Boolean(pushSetting?.enabled)}
          onChange={handleTogglePushEnabled}
        />
        <SettingSwitch
          title={`Incorporate ${displayName} data into Read`}
          description={`Allow Read to access ${displayName} data, including contacts and leads`}
          checked={Boolean(pullSetting?.enabled)}
          onChange={handleTogglePullEnabled}
        />
        <Column spacing={1}>
          <Row variant="apart" spacing={2}>
            <SettingSwitch
              title={`Create new ${displayName} entries`}
              description={`Automatically create a new ${displayName} lead or contact for meeting participants if no existing entry is found`}
              checked={createNewEntriesEnabled}
              onChange={handleToggleCreateNewEntries}
            />
            <Dropdown
              selectedItem={getSelectedFromOptions(
                CONTACT_TYPE_OPTIONS,
                newContactAction
              )}
              name="create-new-entry-type"
              sx={{ backgroundColor: "#FFF" }}
              wrapperSx={{ minWidth: "200px", flexShrink: 0 }}
              onChange={handleChangeContactType}
              placeholder="-"
            >
              {CONTACT_TYPE_OPTIONS.map((option) => (
                <DropdownMenuItem key={option.value} {...option} />
              ))}
            </Dropdown>
          </Row>
          <InfoPanel severity="info" sx={{ ml: "52px" }}>
            When seeking an existing entry, Read will first examine contacts,
            then leads. If none are discovered, Read will generate a new entry
            according to the specified type (lead or contact).
          </InfoPanel>
        </Column>
        <SettingSwitch
          title="Include Meeting Transcript"
          description="Append the full meeting transcript to each Task created"
          checked={Boolean(
            pushSetting?.providerSpecificSettings?.includeTranscript
          )}
          onChange={handleToggleIncludeTranscript}
        />
      </Column>
      <LastPushState
        date={pushSetting?.connectionHealth?.lastSyncTime}
        syncState={pushSetting?.connectionHealth?.syncState}
      />
    </>
  );
};

export default ConnectedContent;
